import { getToken } from 'src/redux/selectors';

const BASE = () =>
  process.env.REACT_APP_GLOBAL_SERVER + 'sproutmanagement/api/checklists/';

// Shared request helper. Rejects with { message, problems? } so screens can
// show the server's own wording — including the list of outstanding items when
// a checklist can't be completed yet.
const request = (method, endpoint, body) =>
  fetch(BASE() + endpoint, {
    method,
    mode: 'cors',
    headers: {
      Authorization: 'Bearer ' + getToken(),
      'Content-Type': 'application/json; charset=utf-8'
    },
    body: body ? JSON.stringify(body) : undefined
  }).then(async res => {
    const text = await res.text();
    let data = null;
    try {
      data = text ? JSON.parse(text) : null;
    } catch (e) {
      data = null;
    }
    if (!res.ok) {
      throw {
        status: res.status,
        message: (data && data.message) || text || 'Something went wrong.',
        problems: (data && data.problems) || []
      };
    }
    return data;
  });

const query = params =>
  Object.keys(params)
    .filter(k => params[k] !== undefined && params[k] !== null && params[k] !== '')
    .map(k => `${encodeURIComponent(k)}=${encodeURIComponent(params[k])}`)
    .join('&');

// The site's local calendar day and timezone, so the server can work out when
// each checklist is due in absolute terms.
export const localDate = (date = new Date()) => {
  const pad = n => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};
const tzOffset = () => new Date().getTimezoneOffset();

// ---------------------------------------------------------------------------
// Library & templates (editor)
// ---------------------------------------------------------------------------

export function findChecklistLibrary() {
  return dispatch =>
    request('GET', 'findlibrary').then(data =>
      dispatch({ type: 'CHECKLISTS_FIND_LIBRARY', data })
    );
}

export function addChecklistFromLibrary(access, libraryKey) {
  return () => request('POST', 'addfromlibrary', { access, libraryKey });
}

export function findChecklistTemplates(access, includeInactive = false) {
  return dispatch =>
    request(
      'GET',
      'findtemplates?' + query({ access, includeInactive: includeInactive ? 'true' : '' })
    ).then(data => dispatch({ type: 'CHECKLISTS_FIND_TEMPLATES', data }));
}

export function saveChecklistTemplate(template) {
  return () => request('POST', 'savetemplate', template);
}

export function deleteChecklistTemplate(id) {
  return () => request('POST', 'deletetemplate', { id });
}

// ---------------------------------------------------------------------------
// Runs (staff)
// ---------------------------------------------------------------------------

// Also creates any of today's scheduled checklists that don't exist yet.
export function findChecklistRuns(access, date = localDate()) {
  return dispatch =>
    request('GET', 'findruns?' + query({ access, date, tzOffset: tzOffset() })).then(
      data => dispatch({ type: 'CHECKLISTS_FIND_RUNS', data: { ...data, date } })
    );
}

export function findChecklistRun(id) {
  return dispatch =>
    request('GET', 'findrun?' + query({ id })).then(data =>
      dispatch({ type: 'CHECKLISTS_UPDATE_RUN', data })
    );
}

export function findChecklistHistory(access, from, to) {
  return dispatch =>
    request('GET', 'findhistory?' + query({ access, from, to })).then(data =>
      dispatch({ type: 'CHECKLISTS_FIND_HISTORY', data })
    );
}

export function startChecklistRun(templateId, fields, date = localDate()) {
  return dispatch =>
    request('POST', 'startrun', { templateId, fields, date, tzOffset: tzOffset() }).then(
      data => dispatch({ type: 'CHECKLISTS_UPDATE_RUN', data })
    );
}

// response: { status: 'yes'|'no'|'na', value, note, photo } — or null to clear.
export function saveChecklistResponse(runId, itemKey, response) {
  return dispatch =>
    request('POST', 'saveresponse', { runId, itemKey, response }).then(data =>
      dispatch({ type: 'CHECKLISTS_UPDATE_RUN', data })
    );
}

// Mark several tick items at once ("All good"). The server only fills items
// that are still unanswered.
export function saveChecklistResponses(runId, keys, status = 'yes') {
  return dispatch =>
    request('POST', 'saveresponses', { runId, keys, status }).then(data =>
      dispatch({ type: 'CHECKLISTS_UPDATE_RUN', data })
    );
}

// ---------------------------------------------------------------------------
// Assigning (managers)
// ---------------------------------------------------------------------------

export function findChecklistAssignees(access) {
  return dispatch =>
    request('GET', 'findassignees?' + query({ access })).then(data =>
      dispatch({ type: 'CHECKLISTS_FIND_ASSIGNEES', data })
    );
}

// Today's run only. assignees: [userId]; sections: { <sectionKey>: [userId] }.
export function reassignChecklistRun(runId, assignees, sections) {
  return dispatch =>
    request('POST', 'reassignrun', { runId, assignees, sections }).then(data =>
      dispatch({ type: 'CHECKLISTS_UPDATE_RUN', data })
    );
}

export function completeChecklistRun(runId, signedOffName) {
  return dispatch =>
    request('POST', 'completerun', { runId, signedOffName }).then(data =>
      dispatch({ type: 'CHECKLISTS_UPDATE_RUN', data })
    );
}
